import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Admin.css";
import edit_svg from "./components/images/edit.svg";
import check_svg from "./components/images/check.svg";
import trash_svg from "./components/images/trash.svg";
import { API_BASE_URL } from "./config";
import InfoIcon from "./components/Info";

function Admin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUserId, setEditUserId] = useState(null); // Track which user is being edited
  const [editedUserData, setEditedUserData] = useState({}); // Track changes to user data for editing
  const [newUserData, setNewUserData] = useState({
    // Track data for adding a new user
    FirstName: "",
    LastName: "",
    EmailAddress: "",
    AdminFlag: false,
  });
  const [addingNewUser, setAddingNewUser] = useState(false); // Track if a new user is being added
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/user`, {
          withCredentials: true, // Include credentials to send the cookie
        });

        if (response.status === 200) {
          const userData = response.data;
          if (!userData.AdminFlag) {
            // If the user is not an admin, navigate away
            navigate("/");
          }
        } else {
          // Handle cases where the response is not OK
          navigate("/");
        }
      } catch (error) {
        console.error("Error verifying admin status", error);
        // Navigate away if there's an error
        navigate("/");
      }
    };

    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/users`, {
          withCredentials: true,
        });
        if (response.status === 200) {
          setUsers(response.data);
        }
      } catch (error) {
        console.error("Error fetching users", error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
    fetchUsers();
  }, [navigate]);

  const handleEditUser = (user) => {
    setEditUserId(user.id);
    setEditedUserData({
      FirstName: user.FirstName,
      LastName: user.LastName,
      EmailAddress: user.EmailAddress,
      AdminFlag: user.AdminFlag,
    });
    setAddingNewUser(false); // Ensure that adding a new user is not active
  };

  const handleCancelEdit = () => {
    setEditUserId(null);
    setEditedUserData({});
    setAddingNewUser(false); // Cancel adding a new user
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedUserData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNewUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewUserData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveChanges = async () => {
    if (addingNewUser) {
      if (!newUserData.FirstName || !newUserData.EmailAddress) {
        alert("First name and email address are required.");
        return;
      }

      try {
        const response = await axios.post(
          `${API_BASE_URL}/add-user`,
          newUserData,
          {
            withCredentials: true,
          }
        );

        if (response.status === 201) {
          const newUser = response.data;

          setUsers((prevUsers) => [
            newUser, // Use the newUser object returned from the backend
            ...prevUsers,
          ]);

          setAddingNewUser(false);
          setNewUserData({
            FirstName: "",
            LastName: "",
            EmailAddress: "",
            AdminFlag: false,
          });
        } else {
          alert("Failed to add user.");
        }
      } catch (error) {
        console.error("Error saving user changes", error);
      }
    } else {
      if (!editedUserData.FirstName || !editedUserData.EmailAddress) {
        alert("First name and email address are required.");
        return;
      }

      try {
        const response = await axios.put(
          `${API_BASE_URL}/user/${editUserId}`,
          editedUserData,
          {
            withCredentials: true,
          }
        );
        if (response.status === 200) {
          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.id === editUserId ? { ...user, ...editedUserData } : user
            )
          );
          setEditUserId(null);
          setEditedUserData({});
        }
      } catch (error) {
        console.error("Error saving user changes", error);
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/user/${userId}`, {
        withCredentials: true,
      });
      if (response.status === 200) {
        setUsers(users.filter((user) => user.id !== userId)); // Update UI by removing deleted user
      }
    } catch (error) {
      console.error("Error deleting user", error);
    }
  };

  const DeleteConfirmationModal = ({ user, onConfirm, onCancel }) => (
    <div className="modal-backdrop">
      <div className="modal-content">
        <h3>Confirm Delete</h3>
        <p>
          Are you sure you want to delete {user.FirstName} {user.LastName}?
        </p>
        <div className="modal-buttons-div">
          <button onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button onClick={() => onConfirm(user.id)} className="delete-btn-modal">
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const beginNewUser = () => {
    setEditUserId(null);
    setNewUserData({
      FirstName: "",
      LastName: "",
      EmailAddress: "",
      AdminFlag: false,
    });
    setAddingNewUser(true);
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  const handleDeleteUserClick = (user) => {
    setShowDeleteModal(true);
    setUserToDelete(user);
  };

  const confirmDeleteUser = async (userId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/user/${userId}`, {
        withCredentials: true,
      });
      if (response.status === 200) {
        setUsers(users.filter((user) => user.id !== userId)); // Update UI by removing deleted user
        setShowDeleteModal(false);
        setUserToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting user", error);
    }
  };

  return (
    <div className="admin-container">
      {showDeleteModal && userToDelete && (
        <DeleteConfirmationModal
          user={userToDelete}
          onConfirm={confirmDeleteUser}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
      <div className="admin-container-header">
        <h2>User Administration</h2>
        <button onClick={beginNewUser} className="header-btn">
          + Add a New User
        </button>
      </div>

      <div className="user-list">
        <div className="user-list-header">
          <div className="user-name">Name</div>
          <div className="user-email">Email Address</div>
          <div className="user-admin">Admin?</div>
          <div className="user-action"></div>
        </div>

        {addingNewUser && (
          <div className="edit-user-form">
            <p>Add User</p>
            <div className="horizontal-line" />
            <div className="edit-input-box-section">
              <div className="edit-input-combo">
                <label>First Name</label>
                <input
                  type="text"
                  name="FirstName"
                  value={newUserData.FirstName}
                  onChange={handleNewUserChange}
                />
              </div>
              <div className="edit-input-combo">
                <label>Last Name</label>
                <input
                  type="text"
                  name="LastName"
                  value={newUserData.LastName}
                  onChange={handleNewUserChange}
                />
              </div>
              <div className="edit-input-combo">
                <label>Email Address</label>
                <input
                  type="email"
                  name="EmailAddress"
                  value={newUserData.EmailAddress}
                  onChange={handleNewUserChange}
                />
              </div>
            </div>
            <label className="custom-checkbox">
              <input
                type="checkbox"
                name="AdminFlag"
                checked={newUserData.AdminFlag}
                onChange={handleNewUserChange}
                style={{ display: "none" }} // Hide the default checkbox
              />
              <span
                className={`checkbox ${newUserData.AdminFlag ? "checked" : ""}`}
              >
                {newUserData.AdminFlag && <img src={check_svg} alt="Checked" />}
              </span>
              <span className="checkbox-label">This user is an admin</span>
            </label>
            <InfoIcon text="Making this user an admin will allow them to edit/delete/create other accounts" />
            <div className="edit-actions">
              <div className="empty"></div>
              <div className="save-cancel-section">
                <button onClick={handleCancelEdit} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handleSaveChanges} className="save-btn">
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {users.map((user) => (
          <div key={user.id}>
            {editUserId === user.id ? (
              <div className="edit-user-form">
                <p>Edit User</p>
                <div className="horizontal-line" />
                <div className="edit-input-box-section">
                  <div className="edit-input-combo">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="FirstName"
                      value={editedUserData.FirstName}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-input-combo">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="LastName"
                      value={editedUserData.LastName}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-input-combo">
                    <label>Email Address</label>
                    <input
                      type="email"
                      name="EmailAddress"
                      value={editedUserData.EmailAddress}
                      onChange={handleEditChange}
                    />
                  </div>
                </div>
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    name="AdminFlag"
                    checked={editedUserData.AdminFlag}
                    onChange={handleEditChange}
                    style={{ display: "none" }} // Hide the default checkbox
                  />
                  <span
                    className={`checkbox ${
                      editedUserData.AdminFlag ? "checked" : ""
                    }`}
                  >
                    {editedUserData.AdminFlag && (
                      <img src={check_svg} alt="Checked" />
                    )}
                  </span>
                  <span className="checkbox-label">This user is an admin</span>
                </label>
                <InfoIcon text="Making this user an admin will allow them to edit/delete/create other accounts" />
                <div className="edit-actions">
                  <button
                    onClick={() => handleDeleteUserClick(user)}
                    className="delete-btn"
                  >
                    <img src={trash_svg} alt="trash icon" />{" "}
                    <p>Delete this User</p>
                  </button>
                  <div className="save-cancel-section">
                    <button onClick={handleCancelEdit} className="cancel-btn">
                      Cancel
                    </button>
                    <button onClick={handleSaveChanges} className="save-btn">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="user-list-item">
                <div className="user-name">
                  {user.FirstName} {user.LastName}
                </div>
                <div className="user-email">{user.EmailAddress}</div>
                <div className="user-admin">
                  {user.AdminFlag ? "Admin" : ""}
                </div>
                <div className="user-action">
                  <img
                    src={edit_svg}
                    onClick={() => handleEditUser(user)}
                    className="edit-icon"
                    alt="Edit"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Admin;
